import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  DropdownContainer,
  DropdownIcon,
  DropdownMenu,
  DropdownOption,
  DropdownTrigger,
} from './styled';
import type { CustomDropdownProps } from './types';

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  size,
  position,
  disabled = false,
  styleProps,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((option) => option.value === value);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  const handleOptionSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          handleToggle();
          break;
        case 'Escape':
          setIsOpen(false);
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            const currentIndex = options.findIndex((opt) => opt.value === value);
            const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
            onChange(options[nextIndex].value);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (isOpen) {
            const currentIndex = options.findIndex((opt) => opt.value === value);
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
            onChange(options[prevIndex].value);
          }
          break;
      }
    },
    [disabled, handleToggle, isOpen, onChange, options, value]
  );

  useEffect(() => {

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <DropdownContainer
      ref={dropdownRef}
      $size={size}
      $position={position}
      $disabled={disabled}
      $styleProps={styleProps}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      role='combobox'
      aria-expanded={isOpen}
      aria-haspopup='listbox'
      aria-label='Select unit'
    >
      <DropdownTrigger $styleProps={styleProps}>
        <span>{selectedOption?.abbreviation ?? selectedOption?.label ?? value}</span>
        <DropdownIcon $open={isOpen} $disabled={disabled}>
          <ChevronDownIcon width={12} height={12} />
        </DropdownIcon>
      </DropdownTrigger>

      <DropdownMenu $open={isOpen} $position={position} $size={size}>
        {options.map((option) => (
          <DropdownOption
            key={option.value}
            $selected={option.value === value}
            $size={size}
            onClick={(e) => {
              e.stopPropagation();
              handleOptionSelect(option.value);
            }}
            role='option'
            aria-selected={option.value === value}
          >
            {option.label}
          </DropdownOption>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  );
};